// src/components/reporting/cohort/CohortThemesReports.js
// Entry point for the Cohort Themes tile: pick a tenant, pick one or more
// cohorts, open the report. SuperAdmin/SuperContentManager only.
//
// Has its own tenant picker rather than using the dashboard's, because the
// dashboard's is SuperAdmin-only (canSelectTenant) and SCM needs to choose a
// tenant here too. Both lists come from this report's own router
// (/api/v1/analytics/cohort-themes/tenants and /cohorts), not the general
// tenant/cohort endpoints, which SCM can't use.
//
// CohortMultiSelect, laid out in the header like CoachCohortReports. Several
// cohorts make one combined report (a comparison would be a separate report);
// one cohort opens that cohort's report, as before.
// Opening a selection only runs a status check; the report is generated from
// the page's Generate button, never automatically.
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { CaretRightIcon } from '@phosphor-icons/react';
import { useAuth } from '../../../contexts/AuthContext';
import apiService from '../../../services/apiService';
import CohortThemesPage from './CohortThemesPage';
import CohortMultiSelect from './CohortMultiSelect';
import cohortSetLabel from './cohortSetLabel';
import '../../../styles/activityReporting.css';

const API_BASE = '/api/v1/analytics/cohort-themes';

const CohortThemesReports = ({ defaultTenant, breadcrumbs = [] }) => {
  const { user } = useAuth();

  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState(defaultTenant?.id ? String(defaultTenant.id) : '');
  const [cohorts, setCohorts] = useState([]);
  const [cohortIds, setCohortIds] = useState([]);
  const [viewing, setViewing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiService.get(`${API_BASE}/tenants`, user);
        if (cancelled || !resp.success) return;
        const list = resp.data.tenants || [];
        setTenants(list);
        // Keep the dashboard's tenant if it's in the list, else the first.
        setTenantId((current) => (list.some((t) => String(t.id) === current) ? current : (list[0] ? String(list[0].id) : '')));
      } catch (err) {
        if (!cancelled) setError('Tenants could not be loaded.');
        console.error('[CohortThemesReports] Failed to load tenants:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const loadCohorts = useCallback(async (tid) => {
    setCohorts([]);
    setCohortIds([]);
    if (!tid) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await apiService.get(`${API_BASE}/cohorts?targetTenantId=${encodeURIComponent(tid)}`, user);
      if (resp.success) setCohorts(resp.data.cohorts || []);
    } catch (err) {
      setError('Cohorts could not be loaded for this tenant.');
      console.error('[CohortThemesReports] Failed to load cohorts:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { loadCohorts(tenantId); }, [tenantId, loadCohorts]);

  const selectedTenant = tenants.find((t) => String(t.id) === tenantId);
  const selectedCohorts = cohorts
    .filter((c) => cohortIds.includes(c.id))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const selectionLabel = cohortSetLabel(selectedCohorts);

  const backToSelection = () => setViewing(false);

  const trail = [
    ...breadcrumbs.slice(0, -1),
    { label: 'Cohort Themes', onClick: viewing ? backToSelection : undefined },
  ];
  if (viewing && selectedCohorts.length > 0) trail.push({ label: selectionLabel });

  const breadcrumb = (
    <div className="arp-breadcrumb">
      {trail.map((crumb, i) => (
        <React.Fragment key={i}>
          {i > 0 && <CaretRightIcon size={11} weight="bold" />}
          {crumb.onClick && i < trail.length - 1 ? (
            <button type="button" className="arp-breadcrumb-link" onClick={crumb.onClick}>{crumb.label}</button>
          ) : (
            <span>{crumb.label}</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );

  if (viewing && selectedCohorts.length > 0 && tenantId) {
    return (
      <div className="activity-report-page activity-report-page--full-height">
        <CohortThemesPage
          cohortIds={selectedCohorts.map((c) => c.id)}
          cohortName={selectionLabel}
          targetTenantId={tenantId}
          breadcrumb={breadcrumb}
        />
      </div>
    );
  }

  return (
    <div className="activity-report-page">
      {breadcrumb}
      <div className="arp-header">
        <div className="arp-header-main">
          <h1 className="arp-title">Cohort Themes</h1>
          <p className="arp-subtitle">
            Who a cohort&apos;s participants are having high-stakes conversations with, and what those conversations are about.
          </p>
        </div>
        <div className="arp-actions arp-header-aside ct-pickers">
          <div className="arp-ms">
            <label className="arp-filter-label" htmlFor="ct-tenant-select">Tenant</label>
            <select
              id="ct-tenant-select"
              className="arp-filter-select ct-tenant-select"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              disabled={tenants.length === 0}
            >
              <option value="" disabled>Select a tenant…</option>
              {tenants.map((t) => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
            </select>
          </div>
          <CohortMultiSelect
            cohorts={cohorts}
            selectedIds={cohortIds}
            onChange={setCohortIds}
            disabled={loading || cohorts.length === 0}
          />
        </div>
      </div>

      {error && <div className="arp-error-state">{error}</div>}

      {loading ? (
        <div className="arp-state">Loading…</div>
      ) : selectedTenant && cohorts.length === 0 && !error ? (
        <div className="arp-state">{selectedTenant.name} has no cohorts yet.</div>
      ) : (
        <div className="arp-button-row">
          <button
            type="button"
            className="arp-btn arp-btn-primary"
            disabled={selectedCohorts.length === 0}
            onClick={() => setViewing(true)}
          >
            Open themes report
          </button>
        </div>
      )}
    </div>
  );
};

CohortThemesReports.propTypes = {
  defaultTenant: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
  breadcrumbs: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    active: PropTypes.bool,
  })),
};

export default CohortThemesReports;